import { useState, useMemo } from 'react'
import {
  Search,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Pencil,
  Check,
  X,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  GROUPING_METHOD_BADGE_VARIANT,
  GROUPING_METHOD_LABEL,
} from '@/components/StoreModels/constants'
import { GroupingTab } from './GroupingTab'
import { useModelGroups } from '@/hooks/useStoreModels'
import { useRenameStoreGroup } from '@/hooks/useStoreGroups'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { formatDateShort } from '@/utils/formatters'
import { cn } from '@/utils/cn'
import type { AxiosError } from 'axios'
import type { StoreModelDetailRead, StoreGroupRead } from '@/types'

type SortKey = 'name' | 'grouping_method' | 'member_count' | 'created_at'
type SortDir = 'asc' | 'desc'

interface GroupsTabProps {
  model: StoreModelDetailRead
  onGroupingApplied?: () => void
}

function TableSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-10 bg-muted rounded" />
      ))}
    </div>
  )
}

export function GroupsTab({ model, onGroupingApplied }: GroupsTabProps) {
  const [search, setSearch] = useState('')
  const [configureOpen, setConfigureOpen] = useState(false)
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null)
  const [editNameInput, setEditNameInput] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const { data, isLoading } = useModelGroups(model.id, search)
  const renameMutation = useRenameStoreGroup()
  const queryClient = useQueryClient()

  const total = data?.pagination.total ?? 0

  const groups = useMemo(() => {
    const sorted = [...(data?.data ?? [])].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name)
          break
        case 'grouping_method':
          cmp = (GROUPING_METHOD_LABEL[a.grouping_method] ?? '').localeCompare(
            GROUPING_METHOD_LABEL[b.grouping_method] ?? ''
          )
          break
        case 'member_count':
          cmp = a.member_count - b.member_count
          break
        case 'created_at':
          cmp = a.created_at.localeCompare(b.created_at)
          break
      }
      return sortDir === 'desc' ? -cmp : cmp
    })
    return sorted
  }, [data?.data, sortKey, sortDir])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const handleGroupingApplied = () => {
    onGroupingApplied?.()
    setConfigureOpen(false)
  }

  const handleStartEdit = (group: StoreGroupRead) => {
    setEditingGroupId(group.id)
    setEditNameInput(group.name)
  }

  const handleCancelEdit = () => {
    setEditingGroupId(null)
    setEditNameInput('')
  }

  const handleSaveEdit = (group: StoreGroupRead) => {
    const trimmed = editNameInput.trim()
    if (!trimmed || trimmed === group.name) {
      handleCancelEdit()
      return
    }

    renameMutation.mutate(
      {
        groupId: group.id,
        payload: { name: trimmed },
        rowVersion: group.row_version,
      },
      {
        onSuccess: () => {
          toast.success('Group renamed successfully')
          setEditingGroupId(null)
          queryClient.invalidateQueries({
            queryKey: queryKeys.storeModels.groups(model.id),
          })
        },
        onError: error => {
          const axiosError = error as AxiosError<{ detail?: string }>
          if (axiosError.response?.status === 409) {
            toast.error(
              axiosError.response.data?.detail ??
                'Conflict: group was modified by another user. Please refresh.'
            )
          } else {
            toast.error(
              axiosError.response?.data?.detail ?? 'Failed to rename group'
            )
          }
        },
      }
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Collapsible Configure Grouping */}
      <Card>
        <CardContent className="p-0">
          <button
            type="button"
            onClick={() => setConfigureOpen(prev => !prev)}
            className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors cursor-pointer"
          >
            {configureOpen ? (
              <ChevronDown className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" />
            )}
            Configure Grouping
          </button>
          <div
            className={cn(
              'overflow-hidden transition-[max-height] duration-300 ease-in-out',
              configureOpen ? 'max-h-[2000px]' : 'max-h-0'
            )}
          >
            <div className="px-4 pb-4">
              <GroupingTab
                model={model}
                onGroupingApplied={handleGroupingApplied}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search + count */}
      {!isLoading && (
        <div className="flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search groups..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {total} group{total !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Table or empty state */}
      {isLoading ? (
        <TableSkeleton />
      ) : groups.length === 0 ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed border-border py-16 text-muted-foreground">
          {search
            ? 'No groups match your search.'
            : 'No groups yet. Configure grouping above to create groups.'}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {(
                [
                  { key: 'name', label: 'Name' },
                  { key: 'grouping_method', label: 'Method' },
                  {
                    key: 'member_count',
                    label: 'Members',
                    className: 'text-right',
                  },
                  { key: 'created_at', label: 'Created' },
                ] as const
              ).map(col => (
                <TableHead
                  key={col.key}
                  className={cn(
                    'cursor-pointer select-none',
                    'className' in col && col.className
                  )}
                  onClick={() => toggleSort(col.key)}
                >
                  <div
                    className={cn(
                      'flex items-center gap-1',
                      col.key === 'member_count' && 'justify-end'
                    )}
                  >
                    {col.label}
                    {sortKey === col.key ? (
                      sortDir === 'asc' ? (
                        <ChevronUp className="h-3.5 w-3.5 text-foreground" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 text-foreground" />
                      )
                    ) : (
                      <ChevronUp className="h-3.5 w-3.5 text-muted-foreground/40" />
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map(group => (
              <TableRow key={group.id}>
                <TableCell className="font-medium">
                  {editingGroupId === group.id ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        value={editNameInput}
                        onChange={e => setEditNameInput(e.target.value)}
                        className="h-8 w-48"
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveEdit(group)
                          if (e.key === 'Escape') handleCancelEdit()
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 cursor-pointer"
                        onClick={() => handleSaveEdit(group)}
                        disabled={renameMutation.isPending}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 cursor-pointer"
                        onClick={handleCancelEdit}
                        disabled={renameMutation.isPending}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 group/name">
                      <span>{group.name}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover/name:opacity-100 transition-opacity cursor-pointer"
                        onClick={() => handleStartEdit(group)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      GROUPING_METHOD_BADGE_VARIANT[group.grouping_method]
                    }
                  >
                    {GROUPING_METHOD_LABEL[group.grouping_method]}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {group.member_count}
                </TableCell>
                <TableCell>{formatDateShort(group.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
